// =============================================================================
// useFocusDetection - React hook voor focus-detectie en -beheer
// =============================================================================
//
// Deze hook detecteert de kern-intentie en focus-categorie voor een doel.
// Kan worden gebruikt in bestaande componenten zonder hun structuur te wijzigen.
//
// Gebruik:
// ```tsx
// const { focus, detection, detectFocus, saveFocus, confirmFocus } = useFocusDetection(
//   goal,
//   milestones,
//   actions
// );
// ```
//
// =============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Goal, Milestone, Action, GoalFocus } from '../types';
import {
  detectGoalFocus,
  createFocusFromDetection,
  DetectionResult,
} from './focusDetection.service';
import {
  getActiveFocusForGoal,
  createGoalFocus,
  confirmFocus as confirmFocusService,
  updateGoalFocus,
} from '../firebase/focus.service';
import {
  getFocusWorthyActions,
  FocusWorthyAction,
} from './focusActions.service';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface UseFocusDetectionResult {
  // Data
  focus: GoalFocus | null;              // Opgeslagen focus (uit Firebase)
  detection: DetectionResult | null;    // Live detectie resultaat
  focusActions: FocusWorthyAction[];    // Focuswaardige acties (gesorteerd)
  topFocusAction: string | null;        // Top 1 focus-actie ID
  
  // State
  isLoading: boolean;                   // Bezig met laden
  isDetecting: boolean;                 // Bezig met detecteren
  isSaving: boolean;                    // Bezig met opslaan
  error: string | null;                 // Error message
  
  // Actions
  detectFocus: () => void;              // Run detectie handmatig
  saveFocus: () => Promise<void>;       // Sla detectie op als nieuwe focus
  confirmFocus: () => Promise<void>;    // Bevestig bestaande focus
  updateFocus: (updates: Partial<GoalFocus>) => Promise<void>; // Update focus
  refreshFocus: () => Promise<void>;    // Herlaad focus uit Firebase
}

// -----------------------------------------------------------------------------
// HOOK
// -----------------------------------------------------------------------------

/**
 * Hook voor focus-detectie en -beheer
 * 
 * @param goal - Het doel om te analyseren
 * @param milestones - Alle mijlpalen (worden gefilterd)
 * @param actions - Alle acties (worden gefilterd)
 * @param autoDetect - Detecteer automatisch bij mount (default: false)
 * @returns UseFocusDetectionResult
 */
export function useFocusDetection(
  goal: Goal | null,
  milestones: Milestone[],
  actions: Action[],
  autoDetect: boolean = false
): UseFocusDetectionResult {
  // State
  const [focus, setFocus] = useState<GoalFocus | null>(null);
  const [detection, setDetection] = useState<DetectionResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing focus from Firebase
  const loadFocus = useCallback(async () => {
    if (!goal || !goal.ownerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const activeFocus = await getActiveFocusForGoal(goal.ownerId, goal.id);
      setFocus(activeFocus);
    } catch (err) {
      console.error('Failed to load focus:', err);
      setError('Kon focus niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [goal]);

  // Detect focus (run detection algorithm)
  const detectFocus = useCallback(() => {
    if (!goal) {
      setDetection(null);
      return;
    }

    setIsDetecting(true);
    setError(null);

    try {
      const result = detectGoalFocus(goal, milestones, actions);
      setDetection(result);
      
      // Log for debugging (internal only)
      if (result) {
        console.log('[Focus Detection]', {
          goalId: goal.id,
          intent: result.intent,
          category: result.category,
          confidence: result.confidence,
          reasoning: result.reasoning,
        });
      } else {
        console.log('[Focus Detection]', {
          goalId: goal.id,
          message: 'No active intent detected',
        });
      }
    } catch (err) {
      console.error('Failed to detect focus:', err);
      setError('Kon focus niet detecteren');
      setDetection(null);
    } finally {
      setIsDetecting(false);
    }
  }, [goal, milestones, actions]);

  // Save detection as new focus
  const saveFocus = useCallback(async () => {
    if (!goal || !goal.ownerId || !detection) {
      throw new Error('Geen doel of detectie beschikbaar');
    }

    setIsSaving(true);
    setError(null);

    try {
      const focusData = createFocusFromDetection(goal.id, goal.ownerId, detection);
      const focusId = await createGoalFocus(goal.ownerId, focusData);
      
      // Reload focus to get the saved version
      const savedFocus = await getActiveFocusForGoal(goal.ownerId, goal.id);
      setFocus(savedFocus);
      
      console.log('[Focus] Saved:', focusId);
    } catch (err) {
      console.error('Failed to save focus:', err);
      setError('Kon focus niet opslaan');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [goal, detection]);

  // Confirm existing focus
  const confirmFocus = useCallback(async () => {
    if (!focus) {
      throw new Error('Geen focus om te bevestigen');
    }

    setIsSaving(true);
    setError(null);

    try {
      await confirmFocusService(focus.id);
      setFocus({ ...focus, confirmedByUser: true });
      
      console.log('[Focus] Confirmed:', focus.id);
    } catch (err) {
      console.error('Failed to confirm focus:', err);
      setError('Kon focus niet bevestigen');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [focus]);

  // Update focus
  const updateFocus = useCallback(async (updates: Partial<GoalFocus>) => {
    if (!focus) {
      throw new Error('Geen focus om te updaten');
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateGoalFocus(focus.id, updates);
      setFocus({ ...focus, ...updates });
      
      console.log('[Focus] Updated:', focus.id);
    } catch (err) {
      console.error('Failed to update focus:', err);
      setError('Kon focus niet updaten');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [focus]);

  // Refresh focus from Firebase
  const refreshFocus = useCallback(async () => {
    await loadFocus();
  }, [loadFocus]);

  // Load focus on mount and when goal changes
  useEffect(() => {
    loadFocus();
  }, [loadFocus]);

  // Auto-detect on mount if enabled
  useEffect(() => {
    if (autoDetect && goal && !detection) {
      detectFocus();
    }
  }, [autoDetect, goal, detection, detectFocus]);

  // Calculate focus-worthy actions
  const focusActions = useMemo(() => {
    if (!goal) return [];
    return getFocusWorthyActions(goal, milestones, actions, focus);
  }, [goal, milestones, actions, focus]);

  // Get top focus action (priority 1)
  const topFocusAction = useMemo(() => {
    return focusActions.length > 0 ? focusActions[0].actionId : null;
  }, [focusActions]);

  return {
    focus,
    detection,
    focusActions,
    topFocusAction,
    isLoading,
    isDetecting,
    isSaving,
    error,
    detectFocus,
    saveFocus,
    confirmFocus,
    updateFocus,
    refreshFocus,
  };
}

// -----------------------------------------------------------------------------
// CONVENIENCE HOOKS
// -----------------------------------------------------------------------------

/**
 * Simple hook: just get the active focus for a goal
 * No detection, just loads from Firebase
 */
export function useGoalFocus(goal: Goal | null): {
  focus: GoalFocus | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [focus, setFocus] = useState<GoalFocus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFocus = useCallback(async () => {
    if (!goal || !goal.ownerId) {
      setFocus(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const activeFocus = await getActiveFocusForGoal(goal.ownerId, goal.id);
      setFocus(activeFocus);
    } catch (err) {
      console.error('Failed to load focus:', err);
      setError('Kon focus niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [goal]);

  useEffect(() => {
    loadFocus();
  }, [loadFocus]);

  return {
    focus,
    isLoading,
    error,
    refresh: loadFocus,
  };
}
